import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PayslipForm, EmployeeDetails } from "../components/PayslipForm";
import { PayslipPreview } from "../components/PayslipPreview";
import { initDB, savePayslipToDB } from "../utils/db";
import { LoginModal } from "../components/LoginModal"; // New Import
import heroImage from "../assets/hero-payslip.jpg";

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [showPreview, setShowPreview] = useState(false);

  // FIX: Initialize state from localStorage so it persists after navigation
  const [user, setUser] = useState<string | null>(() => {
    return localStorage.getItem("payslip_user");
  });

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const [employeeDetails, setEmployeeDetails] =
    useState<EmployeeDetails | null>(null);
  const [letterhead, setLetterhead] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const [primaryColor, setPrimaryColor] = useState<string>("#2563eb");
  const [contentMargin, setContentMargin] = useState<number>(40);
  const [signaturePadding, setSignaturePadding] = useState<number>(20);
  const [historyData, setHistoryData] = useState<any>(null);

  useEffect(() => {
    initDB();
    if (location.state) {
      const {
        loadedData,
        loadedLetterhead,
        loadedSignature,
        loadedColor,
        loadedMargin,
        loadedSignaturePadding,
      } = location.state;
      if (loadedData) {
        setHistoryData({
          details: loadedData,
          letterhead: loadedLetterhead,
          signature: loadedSignature,
          color: loadedColor,
          margin: loadedMargin,
          signaturePadding: loadedSignaturePadding,
        });
        window.history.replaceState({}, document.title);
        setTimeout(() => {
          const formEl = document.querySelector(".form-card");
          if (formEl) formEl.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);

  // FIX: Helper function to handle login and save to storage
  const handleLoginSuccess = (email: string) => {
    setUser(email);
    localStorage.setItem("payslip_user", email);
    setIsLoginModalOpen(false);
  };

  // FIX: Helper function to handle logout and clear storage
  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem("payslip_user");
  };

  const handleGenerate = (
    details: EmployeeDetails,
    letterheadData: string | null,
    signatureData: string | null,
    color: string,
    margin: number,
    sigPadding: number
  ) => {
    // 4. BLOCK GENERATION IF NOT LOGGED IN
    if (!user) {
      setIsLoginModalOpen(true);
      return;
    }

    setEmployeeDetails(details);
    setLetterhead(letterheadData);
    setSignature(signatureData);
    setPrimaryColor(color);
    setContentMargin(margin);
    setSignaturePadding(sigPadding);
    setShowPreview(true);
    window.scrollTo(0, 0);
  };

  const saveCurrentRecordToHistory = async () => {
    if (employeeDetails) {
      await savePayslipToDB(
        employeeDetails,
        letterhead,
        signature,
        primaryColor,
        contentMargin,
        signaturePadding
      );
    }
  };

  const handleBack = () => setShowPreview(false);

  return (
    <div className="app-container">
      {/* 1. UPDATED NAVBAR WITH LOGIN BUTTON */}
      <nav
        style={{
          backgroundColor: "#2563eb",
          borderBottom: "1px solid #e5e7eb",
          padding: "1rem",
        }}
      >
        <div
          style={{
            maxWidth: "1280px",
            margin: "0 auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{ fontWeight: "bold", fontSize: "1.5rem", color: "white" }}
          >
            Payslip<span style={{ color: "#000" }}>Generator</span>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={() => navigate("/history")}
              className="btn"
              style={{ backgroundColor: "#e5e7eb", color: "#2563eb" }}
            >
              View Saved History
            </button>
            {user ? (
              <button
                onClick={handleLogout}
                className="btn"
                style={{ backgroundColor: "#e5e7eb", color: "#dc2626" }}
              >
                Logout
              </button>
            ) : (
              <button
                onClick={() => setIsLoginModalOpen(true)}
                className="btn"
                style={{ backgroundColor: "#e5e7eb", color: "#2563eb" }}
              >
                Login
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* LOGIN MODAL */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      {!showPreview && (
        <div className="hero-section">
          <div className="hero-content">
            <div className="hero-grid">
              <div className="hero-text">
                <h1>Professional Payslip Generator</h1>
                <p>
                  Create beautiful, professional payslips in seconds with your
                  organization's branding.
                </p>
                <div className="hero-features">
                  <div className="feature-item">
                    <div className="feature-icon">✓</div>
                    <div className="feature-content">
                      <h3>Brand-Matched Colors</h3>
                      <p>Upload your letterhead and we'll extract colors.</p>
                    </div>
                  </div>
                  <div className="feature-item">
                    <div className="feature-icon">✓</div>
                    <div className="feature-content">
                      <h3>Smart Leave Management</h3>
                      <p>Calculates deductions based on per-day salary.</p>
                    </div>
                  </div>
                  <div className="feature-item">
                    <div className="feature-icon">✓</div>
                    <div className="feature-content">
                      <h3>Instant PDF Export</h3>
                      <p>High-quality PDF payslips ready for distribution.</p>
                    </div>
                  </div>
                  <div className="feature-item">
                    <div className="feature-icon">✓</div>
                    <div className="feature-content">
                      <h3>Secure Local Storage</h3>
                      <p>Data encrypted and saved locally for 90 days.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hero-image">
                <img src={heroImage} alt="Payslip App" />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="main-content">
        {showPreview && employeeDetails ? (
          <div>
            <button onClick={handleBack} className="btn btn-primary btn-back">
              ← Back to Form
            </button>
            <PayslipPreview
              details={employeeDetails}
              letterhead={letterhead}
              primaryColor={primaryColor}
              contentMargin={contentMargin}
              signature={signature}
              signaturePadding={signaturePadding}
              onSave={saveCurrentRecordToHistory}
            />
          </div>
        ) : (
          <>
            <div className="content-header">
              <h2>Generate Your Payslip</h2>
            </div>
            <PayslipForm
              onGenerate={handleGenerate}
              initialData={historyData?.details}
              initialLetterhead={historyData?.letterhead}
              initialColor={historyData?.color}
              initialMargin={historyData?.margin}
              initialSignature={historyData?.signature}
              initialSignaturePadding={historyData?.signaturePadding}
              key={historyData ? "history-load-" + Date.now() : "default-form"}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Index;
